import { useCallback, useState } from 'react'

import { useAgentFilterSuggestions } from 'features/trend-detection/features/agent-create/hooks/use-agent-create-form/use-agent-filter-suggestions'
import { extractAgentCriteria } from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  FilterCriterion,
  IssueAgentResponse,
} from 'features/trend-detection/types'
import { DEFAULT_AGENT_CONFIG } from 'features/trend-detection/utils/helpers'

export interface UseAgentCreateFormParams {
  service: TrendDetectionService
  projectId: string | null
}

export interface UseAgentCreateFormResult {
  agentName: string
  setAgentName: (name: string) => void
  prompt: string
  setPrompt: (prompt: string) => void
  criteria: FilterCriterion[]
  setCriteria: (criteria: FilterCriterion[]) => void
  /** Names (uuid → theme name) for the theme uuids the last "Suggest
   * filters" call emitted; the filter options may omit them. */
  suggestedLabels: Record<string, string>
  sensitivity: string
  setSensitivity: (value: string) => void
  emailNotifications: boolean
  setEmailNotifications: (value: boolean) => void
  generatingFilters: boolean
  suggestFilters: () => Promise<void>
  ensureFiltersBeforeSubmit: () => Promise<FilterCriterion[]>
  resetForm: () => void
  /** Populates all fields from an existing agent, for the edit-mode create
   * form. Mirrors the deleted `useAgentEdit.initEdit`. */
  hydrateFromAgent: (agent: IssueAgentResponse) => void
}

export function useAgentCreateForm({
  service,
  projectId,
}: UseAgentCreateFormParams): UseAgentCreateFormResult {
  // Starts empty, not pre-filled — the per-type default string is the
  // placeholder only (see Container.tsx's NAME_PLACEHOLDER). The form
  // validates and blocks submit on an empty name; see AgentCreateForm.
  const [agentName, setAgentName] = useState('')
  const [prompt, setPrompt] = useState(DEFAULT_AGENT_CONFIG)
  const [criteria, setCriteria] = useState<FilterCriterion[]>([])
  const [suggestedLabels, setSuggestedLabels] = useState<
    Record<string, string>
  >({})
  const [sensitivity, setSensitivity] = useState('early_signals')
  const [emailNotifications, setEmailNotifications] = useState(false)

  const { generatingFilters, suggestFilters, ensureFiltersBeforeSubmit } =
    useAgentFilterSuggestions({
      service,
      projectId,
      prompt,
      agentName,
      criteria,
      setCriteria,
      setSuggestedLabels,
    })

  const resetForm = useCallback(() => {
    setAgentName('')
    setPrompt(DEFAULT_AGENT_CONFIG)
    setCriteria([])
    setSuggestedLabels({})
    setSensitivity('early_signals')
    setEmailNotifications(false)
  }, [])

  const hydrateFromAgent = useCallback((agent: IssueAgentResponse) => {
    setAgentName(agent.name)
    setPrompt(agent.description ?? agent.config?.prompt ?? '')
    setCriteria(extractAgentCriteria(agent))
    setSensitivity(agent.sensitivity ?? 'early_signals')
    setEmailNotifications(agent.email_notifications)
  }, [])

  return {
    agentName,
    setAgentName,
    prompt,
    setPrompt,
    criteria,
    setCriteria,
    suggestedLabels,
    sensitivity,
    setSensitivity,
    emailNotifications,
    setEmailNotifications,
    generatingFilters,
    suggestFilters,
    ensureFiltersBeforeSubmit,
    resetForm,
    hydrateFromAgent,
  }
}
